import type { ResourceMap } from '@hostesa/i18n';

/**
 * i18n resources for the `onboarding` namespace (website onboarding review
 * wizard: connect → review → activate).
 *
 * The coordinator merges `resources` into the app i18n instance. Non-English
 * locales reuse the English copy as a temporary placeholder so the key-parity
 * check passes; translators replace the strings without changing the key shape.
 */

export const namespace = 'onboarding' as const;

const en = {
  pageTitle: 'Website onboarding',
  pageDescription: 'Paste your hotel website and review the draft your AI receptionist will use.',
  eyebrow: 'Get started',
  steps: {
    connect: 'Connect',
    review: 'Review',
    activate: 'Activate',
    stepLabel: 'Step {{current}} of {{total}}',
  },
  connect: {
    title: 'Connect your website',
    description: 'Enter your hotel website URL. We crawl it and build a draft you can review.',
    urlLabel: 'Hotel website URL',
    urlPlaceholder: 'https://your-hotel.com',
    ownerConfirm: 'I confirm I own or manage this domain',
    start: 'Start onboarding',
    starting: 'Starting…',
    validate: 'Validate URL',
    validating: 'Validating…',
    validated: 'URL validated',
    notValidated: 'URL not yet validated',
    crawl: 'Start crawl',
    crawling: 'Crawling…',
    progressTitle: 'Crawl progress',
    pagesQueued: 'Pages queued',
    pagesFetched: 'Pages fetched',
    itemsExtracted: 'Items extracted',
    domainConfirmed: 'Domain ownership confirmed',
    domainPending: 'Domain ownership not confirmed',
    statusDetail: 'Status detail',
  },
  review: {
    title: 'Review the draft',
    description:
      'Confirm each extracted fact against its source before it reaches the AI. Low-confidence and required items are shown first.',
    empty: 'No draft items to review yet. Start a crawl to extract content.',
    blockingCount: '{{count}} required item still needs review',
    blockingCount_plural: '{{count}} required items still need review',
    blockingCleared: 'All required items reviewed',
    itemType: 'Type',
    itemCategory: 'Category',
    extractedValue: 'Extracted value',
    reviewedValue: 'Your edit',
    confidence: 'Confidence',
    required: 'Required',
    reviewReason: 'Why this needs review',
    noValue: 'No value extracted',
    sources: 'Sources',
    sourceLink: 'View source page',
    accept: 'Accept',
    edit: 'Edit',
    reject: 'Reject',
    editValueLabel: 'Corrected value',
    editValuePlaceholder: 'Enter the correct value',
    editReasonLabel: 'Reason (optional)',
    saveEdit: 'Save edit',
    cancel: 'Cancel',
    reviewed: 'Reviewed',
    itemsReviewed: 'Items reviewed',
    itemsApproved: 'Items approved',
  },
  activate: {
    title: 'Activate',
    description: 'Once required fields are confirmed, approve and go live.',
    approve: 'Approve draft',
    approving: 'Approving…',
    activate: 'Activate hotel',
    activating: 'Activating…',
    approved: 'Draft approved',
    activated: 'Hotel activated',
    gateTitle: 'Activation gate',
    gateDescription: 'Every check must pass before the receptionist goes live.',
    gateProgress: 'Activation progress',
    requiredFieldsConfirmed: 'Required fields confirmed',
    retrievalSmokePassed: 'Retrieval smoke test passed',
    syntheticCallPassed: 'Synthetic call passed',
    checkPassed: 'Passed',
    checkPending: 'Pending',
    blockedByReview: 'Resolve all required draft items before approving.',
  },
  itemType: {
    profile: 'Profile',
    faq: 'FAQ',
    document: 'Document',
  },
  itemStatus: {
    pending_review: 'Pending review',
    accepted: 'Accepted',
    edited: 'Edited',
    rejected: 'Rejected',
  },
  runStatus: {
    created: 'Created',
    url_validated: 'URL validated',
    crawling: 'Crawling',
    draft_ready: 'Draft ready',
    in_review: 'In review',
    approved: 'Approved',
    indexing: 'Indexing',
    validating: 'Validating',
    ready_for_test: 'Ready for test',
    activated: 'Activated',
    failed: 'Failed',
    rejected: 'Rejected',
    expired: 'Expired',
  },
  runs: {
    title: 'Onboarding runs',
    current: 'Current run',
    startedAt: 'Started',
    none: 'No onboarding runs yet.',
    newRun: 'Start a new run',
  },
  states: {
    loading: 'Loading onboarding…',
    errorTitle: 'Could not load onboarding',
    errorDescription: 'Something went wrong while loading onboarding. Please try again.',
    retry: 'Retry',
  },
};

export const resources: ResourceMap = {
  en: { [namespace]: en },
  bg: { [namespace]: en },
  de: { [namespace]: en },
  fr: { [namespace]: en },
};

export default { namespace, resources };
